import React from "react";
import { Transaction } from "@common/types/transaction";
import { formatBusinessDate, formatDateOnly } from "@helpers/date";
import { getTransactionAccountNameWithLast4 } from "@common/helpers/transactions";
import { getLeadingAccountConnection } from "@common/helpers/financialAccount";
import { useBreakableText } from "@hooks/useBreakableText";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { TransactionAmount } from "../../TransactionAmount";
import { TransactionInstitution } from "../TransactionInstitution";
import styles from "./SimplifedTransactionDetails.module.scss";

interface Props {
    transaction: Transaction;
}

export const SimplifiedTransactionDetails: React.FC<Props> = ({
    transaction,
}) => {
    const breakableDescription = useBreakableText(
        transaction.displayDescription ?? transaction.bankDescription,
    );
    const connection = getLeadingAccountConnection(
        transaction.financialAccount,
    );

    return (
        <article className={styles.details}>
            <header className={styles.header}>
                <div className={styles.description}>{breakableDescription}</div>

                <TransactionAmount
                    transaction={transaction}
                    color
                    addSign
                    className={styles.transactionAmount}
                />
            </header>

            <main className={styles.info}>
                <section>
                    <header className="label">Date</header>
                    <div>
                        {transaction.dateOnly
                            ? formatDateOnly(
                                  transaction.dateOnly,
                                  "MMMM d, ''yy",
                              )
                            : formatBusinessDate(
                                  transaction.date,
                                  "MMMM d, ''yy",
                              )}
                    </div>
                </section>

                <section>
                    <header className="label">Entity</header>
                    <div className={styles.valueWithIcon}>
                        <EntityAvatar
                            entity={transaction.entity}
                            size="xs"
                            aria-hidden
                        />

                        {transaction.entity.name}
                    </div>
                </section>

                {connection ? (
                    <section>
                        <header className="label">Institution</header>
                        <div className={styles.valueWithIcon}>
                            <TransactionInstitution transaction={transaction} />
                        </div>
                    </section>
                ) : null}

                <section>
                    <header className="label">Bank description</header>
                    <div>{transaction.bankDescription}</div>
                </section>

                <section>
                    <header className="label">Account</header>
                    <div>
                        {getTransactionAccountNameWithLast4(transaction, 2)}
                    </div>
                </section>
            </main>
        </article>
    );
};
